/**
 * Governed transactions — the client side of migration 749.
 *
 * Five acts used to run from the browser as a client-side policy check
 * followed by one or more direct writes, so a failure part-way left half the
 * act behind and the database never decided who was allowed. Each is now ONE
 * SECURITY DEFINER RPC bound to the signed-in person (build contract §2.3a);
 * this module is the only typed way to call them.
 *
 *   createItemPoolRpc      create an item pool (one live pool per track)
 *   setItemPoolLock        lock / unlock an item pool (unlock needs a reason)
 *   setSessionCutLine      move a run session's cut line — a durable Decision
 *   overrideSessionRank    a manager override of one item's rank — reason, Decision, group notice (768)
 *   setItemPoolCutLine     move an item pool's cut line — a durable Decision (760)
 *   launchCycleDesign      launch a Cycle Designer design, all or nothing
 *   materializeBaselinePlan  generate tracks from the baseline, all or nothing
 *   resolveItemCluster     Doc 12 Rule 1 cluster resolution + its Decision
 *
 * Every RPC returns `{ success, error?, code?, blocked_by?, remedy?, ... }` and
 * refuses BEFORE writing anything, so `ok === false` always means nothing
 * changed. The envelope mapping is 738's `mapReversalResult` — only a literal
 * `success: true` counts as success.
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';
import { mapReversalResult, type ReversalResult } from './reversals/reversalsService';
import type { Json } from '@/types/database.generated';
import type { RationaleParts } from './rationaleParts';

export type GovernedResult = ReversalResult;

type RpcEnvelope = { data: unknown; error: { message?: string } | null };

async function call(run: () => PromiseLike<RpcEnvelope>): Promise<GovernedResult> {
  try {
    const { data, error } = await run();
    return mapReversalResult(data, error);
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/** Lock (true) or unlock (false) an item pool. Unlocking requires a written reason. */
export function setItemPoolLock(poolId: string, locked: boolean, reason?: string | null): Promise<GovernedResult> {
  if (!locked && !(reason ?? '').trim()) {
    return Promise.resolve({
      ok: false,
      error: 'A written reason is required to unlock a pool — unlocking reopens membership without an amendment.',
    });
  }
  return call(() =>
    getTypedSupabaseClient().rpc('set_item_pool_lock', {
      p_pool_id: poolId,
      p_locked: locked,
      p_reason: reason?.trim() || undefined,
    }),
  );
}

export interface CreateItemPoolInput {
  poolId: string;
  name: string;
  workspaceId?: string | null;
  description?: string | null;
  cycleId?: string | null;
  trackId?: string | null;
  tags?: string[];
  cutLinePosition?: number;
}

/**
 * Create an item pool as the signed-in person (761): scope checks, authority,
 * the one-live-pool-per-track refusal (blockedBy 'item_pools' + remedy), the
 * insert and the ItemPoolCreated event in one transaction.
 */
export function createItemPoolRpc(input: CreateItemPoolInput): Promise<GovernedResult> {
  return call(() =>
    getTypedSupabaseClient().rpc('create_item_pool', {
      p_pool_id: input.poolId,
      p_name: input.name,
      p_workspace_id: input.workspaceId || undefined,
      p_description: input.description?.trim() || undefined,
      p_cycle_id: input.cycleId || undefined,
      p_track_id: input.trackId || undefined,
      p_tags: input.tags ?? [],
      p_cut_line_position: input.cutLinePosition ?? 0,
    }),
  );
}

/** The four-part rationale as the RPCs take it (migration 985): absent when nothing was written. */
function rationalePartsArg(parts?: RationaleParts | null): Json | undefined {
  return parts && Object.keys(parts).length > 0 ? (parts as Json) : undefined;
}

/**
 * Move a RUN_COMPLETE session's cut line (facilitator or workspace admin).
 * `rationaleParts` (985) lands on the cut-line Decision's four rationale
 * columns and review date; the one-line `rationale` stays its reasoning.
 */
export function setSessionCutLine(
  sessionId: string,
  position: number,
  rationale?: string | null,
  rationaleParts?: RationaleParts | null,
): Promise<GovernedResult> {
  return call(() =>
    getTypedSupabaseClient().rpc('set_session_cut_line', {
      p_session_id: sessionId,
      p_position: position,
      p_rationale: rationale?.trim() || undefined,
      p_rationale_parts: rationalePartsArg(rationaleParts),
    }),
  );
}

/**
 * Override one item's rank in a run session (migration 768 — the canon 01
 * Override stage). Needs the "Change ranking order" permission (RANK_OVERRIDE,
 * mig 891; workspace admins and the cycle owner too — not the facilitator as
 * such) and the session-defaults switch on (code 'switch_off' otherwise);
 * RUN_COMPLETE only; a written reason is required. The server moves the item, appends the
 * RankOverride the whole group sees in the results, renumbers the results,
 * records a Decision and notifies the session's people, in one transaction.
 * `rationaleParts` (985) — context, alternatives, what would make it worth
 * revisiting, a review date — lands on that Decision; the reason is its
 * reasoning.
 */
export function overrideSessionRank(
  sessionId: string,
  itemId: string,
  newRank: number,
  reason: string,
  rationaleParts?: RationaleParts | null,
): Promise<GovernedResult> {
  if (!reason.trim()) {
    return Promise.resolve({
      ok: false,
      error: 'A written reason is required to override a ranking.',
    });
  }
  return call(() =>
    getTypedSupabaseClient().rpc('override_session_rank', {
      p_session_id: sessionId,
      p_item_id: itemId,
      p_new_rank: newRank,
      p_reason: reason.trim(),
      p_rationale_parts: rationalePartsArg(rationaleParts),
    }),
  );
}

/**
 * Move an item pool's cut line (migration 760): any active member of the
 * pool's workspace; a PUBLISHED pool needs a written rationale (the server
 * refuses with `code: 'reason_required'`, canon 27). Records an
 * item_pool_cut_line_set event and a range_selection Decision.
 * `rationaleParts` (985) lands on that Decision's rationale columns; its
 * reasoning also meets a published pool's required reason.
 */
export function setItemPoolCutLine(
  poolId: string,
  position: number,
  rationale?: string | null,
  rationaleParts?: RationaleParts | null,
): Promise<GovernedResult> {
  return call(() =>
    getTypedSupabaseClient().rpc('set_item_pool_cut_line', {
      p_pool_id: poolId,
      p_position: position,
      p_rationale: rationale?.trim() || undefined,
      p_rationale_parts: rationalePartsArg(rationaleParts),
    }),
  );
}

/** Launch a Cycle Designer design in one transaction. See launchCycleDesigner.ts for the config shape. */
export function launchCycleDesign(cycleId: string, config: Record<string, unknown>): Promise<GovernedResult> {
  return call(() =>
    getTypedSupabaseClient().rpc('launch_cycle_design', { p_cycle_id: cycleId, p_config: config as Json }),
  );
}

/** Apply a baseline-template materialization plan in one transaction. */
export function materializeBaselinePlan(cycleId: string, plan: Record<string, unknown>): Promise<GovernedResult> {
  return call(() =>
    getTypedSupabaseClient().rpc('materialize_baseline_tracks', { p_cycle_id: cycleId, p_plan: plan as Json }),
  );
}

/** Doc 12 Rule 1: resolve a cluster as duplicate / variant / reframe. */
export function resolveItemCluster(
  groupId: string,
  resolution: 'duplicate' | 'variant' | 'reframe',
  keepItemId: string | null | undefined,
  rationale: string,
): Promise<GovernedResult> {
  return call(() =>
    getTypedSupabaseClient().rpc('resolve_item_cluster', {
      p_group_id: groupId,
      p_resolution: resolution,
      // The RPC ignores the kept item for `variant`; the generated Args type is non-null.
      p_keep_item_id: keepItemId ?? (null as unknown as string),
      p_rationale: rationale,
    }),
  );
}
